/**
 * Hidden Field Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect } from '../../format-utils'
import type { VariableSelectValue } from '../../types'
import { deepMerge } from '../../parser-utils'
import hiddenFieldTemplate from './hidden-field.template.json'

export interface IHiddenFieldComponentSettings {
  // Data settings
  'hidden-field-data-value': string // Column ID
  'hidden-field-data-attribute': string // Attribute name
  'hidden-field-data-default-value': VariableSelectValue
}

export interface IHiddenFieldComponent extends IBuilderComponent {
  componentId: 'hidden-field'
  groupId: 'form'
  settings: IHiddenFieldComponentSettings
}

/**
 * Hidden Field AI Input
 */
export interface AiHiddenFieldInput {
  columnId: string // Required: maps to table column
  attribute?: string // Attribute name (e.g., "Created At", "User ID")
  defaultValue?: string // Default value
  useCurrentDate?: boolean // Use current date as default
  useCurrentUser?: boolean // Use current user as default
}

const HIDDEN_FIELD_DEFAULT_TEMPLATE = hiddenFieldTemplate as unknown as IHiddenFieldComponentSettings

export class HiddenFieldComp {
  getTemplate(): IHiddenFieldComponentSettings {
    return HIDDEN_FIELD_DEFAULT_TEMPLATE
  }

  /**
   * format hidden field component settings to AI hidden field input
   */
  format(settings: IHiddenFieldComponentSettings): AiHiddenFieldInput {
    const result: AiHiddenFieldInput = {
      columnId: settings['hidden-field-data-value'],
    }

    // Attribute
    if (settings['hidden-field-data-attribute']) {
      result.attribute = settings['hidden-field-data-attribute']
    }

    // Default Value - check flag to determine type
    const defaultValue = settings['hidden-field-data-default-value']
    if (defaultValue) {
      const flag = defaultValue.flag
      
      // Check for CurrentDateTime flag (131072)
      if (flag === 131072) {
        result.useCurrentDate = true
      }
      // Check for CurrentUser flag (65536)
      else if (flag === 65536) {
        result.useCurrentUser = true
      }
      // Otherwise, format as regular defaultValue string
      else {
        const formattedValue = formatFromVariableSelect(defaultValue)
        if (formattedValue) {
          result.defaultValue = formattedValue
        }
      }
    }

    return result
  }

  /**
   * parse AI hidden field input to hidden field component settings
   */
  parse(input: AiHiddenFieldInput): IHiddenFieldComponentSettings {
    const result = deepMerge(HIDDEN_FIELD_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['hidden-field-data-value'] = input.columnId
    }

    // Data - Attribute
    if (input.attribute) {
      result['hidden-field-data-attribute'] = input.attribute
    }

    // Data - Default Value
    if (input.useCurrentDate) {
      // Use CurrentDate flag (131072)
      result['hidden-field-data-default-value'] = {
        flag: 131072, // CurrentDate flag
        id: uuidv4(),
        // @ts-ignore
        value: '',
      }
    } else if (input.useCurrentUser) {
      // Use CurrentUser flag (65536)
      result['hidden-field-data-default-value'] = {
        flag: 65536, // CurrentUser flag
        id: uuidv4(),
        // @ts-ignore
        value: '',
      }
    } else if (input.defaultValue !== undefined) {
      // Use custom text
      result['hidden-field-data-default-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.defaultValue,
      }
    }

    return result
  }
}
